import { useState } from 'react';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Button } from '@/shared/components/ui/Button';
import { QuestionShell } from '../QuestionShell';
import { describeStrings } from '../../utils/describe.strings';
import type { FollowUpAnswer } from '../../types/describe.types';

interface FollowUpQuestionProps {
  question: string;
  index: number;
  status: 'active' | 'answered' | 'upcoming';
  isLast: boolean;
  answer: FollowUpAnswer | null;
  onSubmit: (value: { index: number; answer: string }) => void;
}

export const FollowUpQuestion = ({
  question,
  index,
  status,
  isLast,
  answer,
  onSubmit,
}: FollowUpQuestionProps) => {
  if (status === 'answered' && answer) {
    return (
      <QuestionShell
        kind="follow-up"
        heading={answer.question}
        status="answered"
        isLast={isLast}
      >
        <p className="text-sm text-slate-700 leading-relaxed max-w-md">
          {answer.answer}
        </p>
      </QuestionShell>
    );
  }

  return (
    <QuestionShell
      kind="follow-up"
      heading={question}
      status={status}
      isLast={isLast}
    >
      <FollowUpEditor
        initial={answer?.answer ?? ''}
        onSubmit={(value) => onSubmit({ index, answer: value })}
      />
    </QuestionShell>
  );
};

function FollowUpEditor({
  initial,
  onSubmit,
}: {
  initial: string;
  onSubmit: (value: string) => void;
}) {
  const [value, setValue] = useState(initial);

  const submit = () => {
    if (!value.trim()) return;
    onSubmit(value.trim());
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className="space-y-3">
      <Textarea
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={describeStrings.followUps.placeholder}
        rows={3}
        className="w-full bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-900 resize-none shadow-sm focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:border-primary/60"
        autoFocus
      />
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">
          {describeStrings.followUps.submitHint}
        </p>
        <Button type="button" onClick={submit} disabled={!value.trim()}>
          {describeStrings.common.continue}
        </Button>
      </div>
    </div>
  );
}
